import { createClient, SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';
import { ChatMessage, UserProfile } from '../types';

// Supabase configuration with environment variables and defaults provided
export const SUPABASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL) ||
  (typeof process !== 'undefined' && (process.env?.NEXT_PUBLIC_SUPABASE_URL || process.env?.SUPABASE_URL)) ||
  'https://mqrdzwexqkbsdfslpczo.supabase.co';

export const SUPABASE_ANON_KEY =
  (typeof import.meta !== 'undefined' &&
    (import.meta.env?.VITE_SUPABASE_ANON_KEY ||
      import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY)) ||
  (typeof process !== 'undefined' &&
    (process.env?.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
      process.env?.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ||
      process.env?.SUPABASE_ANON_KEY)) ||
  'sb_publishable_tbz9z-xV_T7kZSwiY3bD6w_peqJ6-p6';

// Global singleton Supabase Client
export const supabase: SupabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 20,
    },
  },
});

/**
 * Realtime channel for Wateen Chat Messages
 */
export function subscribeToWateenChannel(
  channelName: string,
  callbacks: {
    onMessage?: (message: ChatMessage) => void;
    onTyping?: (data: { username: string; isTyping: boolean; conversationId: string }) => void;
    onReaction?: (data: { messageId: string; emoji: string; username: string }) => void;
    onPresence?: (presenceState: Record<string, any>) => void;
  }
): RealtimeChannel {
  const channel = supabase.channel(channelName, {
    config: {
      broadcast: { self: true },
      presence: { key: channelName },
    },
  });

  if (callbacks.onMessage) {
    channel.on('broadcast', { event: 'new_message' }, ({ payload }) => {
      if (payload && payload.message) {
        callbacks.onMessage?.(payload.message);
      }
    });

    // Also listen to database postgres_changes if table exists
    channel.on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'messages' },
      (payload) => {
        if (payload.new) {
          const msg = payload.new as any;
          const formatted: ChatMessage = {
            id: msg.id || `msg-${Date.now()}`,
            conversationId: msg.conversation_id || msg.conversationId,
            senderId: msg.sender_id || msg.senderId,
            senderUsername: msg.sender_username || msg.senderUsername,
            senderName: msg.sender_name || msg.senderName,
            senderAvatar: msg.sender_avatar || msg.senderAvatar,
            text: msg.text || '',
            timestamp: msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now(),
            attachments: msg.media_url
              ? [
                  {
                    id: `att-${Date.now()}`,
                    type: (msg.media_type as any) || 'image',
                    url: msg.media_url,
                    name: 'file',
                  },
                ]
              : msg.attachments || undefined,
            replyTo: msg.reply_to_id
              ? {
                  id: msg.reply_to_id,
                  senderName: msg.reply_to_sender || '',
                  senderUsername: msg.reply_to_sender || '',
                  text: msg.reply_to_text || '',
                }
              : undefined,
            isEdited: Boolean(msg.is_edited || msg.isEdited),
            reactions: Array.isArray(msg.reactions) ? msg.reactions : undefined,
            isPinned: Boolean(msg.is_pinned || msg.isPinned),
            status: 'delivered',
          };
          callbacks.onMessage?.(formatted);
        }
      }
    );
  }

  if (callbacks.onTyping) {
    channel.on('broadcast', { event: 'typing' }, ({ payload }) => {
      if (payload) {
        callbacks.onTyping?.(payload);
      }
    });
  }

  if (callbacks.onReaction) {
    channel.on('broadcast', { event: 'reaction' }, ({ payload }) => {
      if (payload) {
        callbacks.onReaction?.(payload);
      }
    });
  }

  if (callbacks.onPresence) {
    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        callbacks.onPresence?.(state);
      })
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        const state = channel.presenceState();
        callbacks.onPresence?.(state);
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        const state = channel.presenceState();
        callbacks.onPresence?.(state);
      });
  }

  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      console.log(`[Supabase Realtime] Subscribed to channel: ${channelName}`);
    }
  });

  return channel;
}

/**
 * Broadcast message via Supabase Realtime
 */
export async function broadcastMessageToSupabase(channelName: string, message: ChatMessage) {
  try {
    const channel = supabase.channel(channelName);
    await channel.send({
      type: 'broadcast',
      event: 'new_message',
      payload: { message },
    });
  } catch (err) {
    console.warn('[Supabase] Failed to broadcast message:', err);
  }
}

/**
 * Broadcast typing indicator via Supabase Realtime
 */
export async function broadcastTypingToSupabase(
  channelName: string,
  data: { username: string; isTyping: boolean; conversationId: string }
) {
  try {
    const channel = supabase.channel(channelName);
    await channel.send({
      type: 'broadcast',
      event: 'typing',
      payload: data,
    });
  } catch (err) {
    // Ignore transient network errors
  }
}

/**
 * Supabase Auth: Register new user
 */
export async function registerWithSupabase(email: string, password: string, metadata?: Record<string, any>) {
  return await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata,
    },
  });
}

/**
 * Supabase Auth: Login user
 */
export async function loginWithSupabase(email: string, password: string) {
  return await supabase.auth.signInWithPassword({
    email,
    password,
  });
}

/**
 * Supabase Auth: Logout user
 */
export async function logoutWithSupabase() {
  return await supabase.auth.signOut();
}

/**
 * Check active Supabase session
 */
export async function getSupabaseSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) return null;
  return data.session;
}

/**
 * Search user profiles in Supabase
 */
export async function searchProfilesInSupabase(query: string) {
  try {
    const cleanQ = query.trim().replace(/^@/, '');
    if (!cleanQ) return [];

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .or(`username.ilike.%${cleanQ}%,display_name.ilike.%${cleanQ}%,email.ilike.%${cleanQ}%`)
      .limit(10);

    if (error) {
      return [];
    }
    return data || [];
  } catch {
    return [];
  }
}

